import axios from "axios";
import { compareDateByType } from "../../components/Project/listOrdering";
import { Drawing2dImage } from "@/models/Drawing2DImage";
import localDb from "../../utils/local-db";

const state = {
	drawing2dImageList: [],
	pointsOfTwoPointAlignment: [],
	isTwoPointAlignment: false,
	imageUnder2PointAlignment: null,
	imageUnderEdit: null,
};

const getters = {
	getDrawing2dImageList: (state) => state.drawing2dImageList,
	getActiveDrawing2dImageList: (state) => {
		return state.drawing2dImageList.filter((drawing2dImage) =>
			drawing2dImage.getIsVisible(),
		);
	},
};

const mutations = {
	addDrawing2dImage: (state, drawing2dImage) => {
		state.drawing2dImageList = [...state.drawing2dImageList, drawing2dImage];
	},
	// 受け取った配列で置き換える。既存インスタンスの再利用は action 側で update(row) により行う
	updateDrawing2dImageList: (state, drawing2dImageList) => {
		const nextList = [...drawing2dImageList];
		nextList.sort(compareDateByType);
		state.drawing2dImageList = nextList;
	},
	deleteDrawing2dImageListStateById: (state, assetId) => {
		const drawing2dImageList = state.drawing2dImageList.filter(
			(drawing2dImage) => drawing2dImage.assetId !== assetId,
		);
		state.drawing2dImageList = drawing2dImageList;
	},
	setIsTwoPointAlignment: (state, twoPointObj) => {
		state.isTwoPointAlignment = twoPointObj.flag;
		state.imageUnder2PointAlignment = twoPointObj.imageUnder2PointAlignment;
	},
	addPointForTwoPointsAlignment: (state, pointObj) => {
		const newArray = [...state.pointsOfTwoPointAlignment];
		newArray[pointObj.index] = pointObj;
		newArray[pointObj.index].disabled = false;
		state.pointsOfTwoPointAlignment = newArray;
	},
	deletePointForTwoPointsAlignmentByIndex: (state, index) => {
		const newArray = [...state.pointsOfTwoPointAlignment];
		newArray[index].disabled = true;
		state.pointsOfTwoPointAlignment = newArray;
	},
	deleteAllPointsForTwoPointsAlignment: (state) => {
		state.pointsOfTwoPointAlignment = [];
	},
	setImageUnderEdit: (state, drawing2dImage) => {
		state.imageUnderEdit = drawing2dImage;
	},
};

const actions = {
	async addDrawing2dImage({ commit, rootState }, { drawing2dImage }) {
		commit("addDrawing2dImage", drawing2dImage);
		await localDb.deleteDrawing2DImage(
			rootState.user.id,
			rootState.site.site_uuid,
			drawing2dImage.asset_id,
		);
	},
	async getDrawing2dImageList({ commit, state, rootState }, site_id) {
		// call backend api to get all drawing 2d image list
		const res = await axios.get(
			`${import.meta.env.VITE_API_BASE}/drawing2dImage/${site_id}`,
			{
				auth: rootState.authObject,
			},
		);
		const drawing2dImageList = [];
		for (const row of res.data) {
			// 既存インスタンスは update(row) で更新して UI 状態を保つ（get_point_cloud_list と同規約）
			const existing = state.drawing2dImageList.find(
				(image) => image.assetId === row.id,
			);
			if (existing) {
				existing.update(row);
				drawing2dImageList.push(existing);
				continue;
			}
			// 新規インスタンスには UI 既定値だけを与え、
			// サーバ由来のフィールドの写像は update(row) に集約する（列の増減で二重管理にしない）
			const created = new Drawing2dImage({
				file: null,
				assetId: row.id,
				transparency: 0.5,
				isVisible: false,
			});
			created.update(row);
			drawing2dImageList.push(created);
		}
		commit("updateDrawing2dImageList", drawing2dImageList);
	},
	async deleteDrawing2dImage(
		{ commit, rootState },
		{ siteId, drawing2dImage },
	) {
		const assetId = drawing2dImage.assetId ?? drawing2dImage.asset_id;
		try {
			await axios.delete(
				`${import.meta.env.VITE_API_BASE}/drawing2dImage/${siteId}/${assetId}`,
				{
					auth: rootState.authObject,
				},
			);
			await localDb.deleteDrawing2DImage(
				rootState.user.id,
				rootState.site.site_uuid,
				assetId,
			);
		} catch (e) {
			console.warn(e);
			throw e;
		}
		commit("deleteDrawing2dImageListStateById", assetId);
	},
	setIsTwoPointAlignment: ({ commit }, { flag, imageUnder2PointAlignment }) => {
		commit("setIsTwoPointAlignment", { flag, imageUnder2PointAlignment });
	},
	addPointForTwoPointsAlignment: ({ commit }, { pointInfo }) => {
		commit("addPointForTwoPointsAlignment", pointInfo);
	},
	deletePointForTwoPointsAlignmentByIndex: ({ commit }, { index }) => {
		commit("deletePointForTwoPointsAlignmentByIndex", index);
	},
	deleteAllPointsForTwoPointsAlignment: ({ commit }) => {
		commit("deleteAllPointsForTwoPointsAlignment");
	},

	async startEdit(
		{ state, commit, dispatch, rootState },
		{ drawing2dImage, onError, onCancel, onFinish },
	) {
		const drawingId = drawing2dImage.getId();

		// If there is an image or object under edit, or 2-point alignment is in progress, end it first
		if (
			state.imageUnderEdit ||
			rootState.object?.objectUnderEdit ||
			state.isTwoPointAlignment
		) {
			const ended = await dispatch("inProgressModeManager/endEdit", null, {
				root: true,
			});
			if (!ended) return;
		}

		const editStarted = await dispatch(
			"inProgressModeManager/startEdit",
			{ objectType: "drawing_2d_image", objectId: drawingId },
			{ root: true },
		);
		if (!editStarted) return;
		commit("setImageUnderEdit", drawing2dImage);
		try {
			await rootState.viewer.startEditDrawing2dImage(drawingId, {
				onError,
				onCancel,
				onFinish,
			});
		} catch (error) {
			// Rollback on failure
			commit("setImageUnderEdit", null);
			await dispatch("inProgressModeManager/endEdit", null, { root: true });

			if (onError) onError(error);
			throw error;
		}
	},

	async endEdit({ state, commit, rootState, dispatch }) {
		if (!state.imageUnderEdit) return;
		try {
			await rootState.viewer.endEditDrawing2dImage();
			if (state.imageUnderEdit) {
				const elevation = rootState.viewer.getElevationOfDrawing2dImage(
					state.imageUnderEdit.getId(),
				);
				if (elevation !== undefined && elevation !== null) {
					state.imageUnderEdit.setElevation(elevation);
				}
			}
		} catch (e) {
			console.warn("Failed to end edit for drawing2dImage:", e);
			throw e;
		} finally {
			commit("setImageUnderEdit", null);
		}
	},
	async start2pointsAlignment(
		{ state, commit, rootState },
		{ drawing2dImage, onStart, onError, onCancel, onFinish },
	) {
		commit("setIsTwoPointAlignment", {
			flag: true,
			imageUnder2PointAlignment: drawing2dImage,
		});
		try {
			await rootState.viewer.start2PointsAlignment(drawing2dImage.getId(), {
				onStart,
				onError,
				onCancel,
				onFinish,
			});
		} catch (e) {
			commit("setIsTwoPointAlignment", {
				flag: false,
				imageUnder2PointAlignment: null,
			});
			throw e;
		}
	},
	async end2pointsAlignment({ state, commit, rootState }, { twoPointArgs }) {
		if (state.imageUnder2PointAlignment) {
			await rootState.viewer.end2PointsAlignment({ twoPointArgs });
			commit("setIsTwoPointAlignment", {
				flag: false,
				imageUnder2PointAlignment: null,
			});
		}
	},
	async cancel2pointsAlignment({ state, commit, rootState }) {
		if (state.imageUnder2PointAlignment) {
			await rootState.viewer.cancel2PointsAlignment();
			commit("setIsTwoPointAlignment", {
				flag: false,
				imageUnder2PointAlignment: null,
			});
		}
	},
	async updateDestinationPosition({ state, rootState }, { position }) {
		if (state.isTwoPointAlignment) {
			await rootState.viewer.updateDestinationPositionFor2PointsAlignment(
				position,
			);
		}
	},
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
};
